import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  EditIcon,
  EmptyState,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import {
  use_DeleteTeachingBlockAvailabilityExclusionMutation,
  use_DeleteTeachingBlockCardsMutation,
  use_DeleteTeachingBlocksMutation,
  use_DeleteTeachingBlockVersionsMutation,
  useDuplicateTeachingBlockVersionMutation,
  useTeachingBlockDraftQuery,
  useUpdateTeachingBlockVersionsMutation,
} from '../../../types/planung-graphql-client-defs';
import {
  showSuccessDeleteToast,
  showSuccessDuplicateToast,
  showSuccessSaveToast,
  showUserErrorToast,
} from '../../../utils/toast';
import { TeachingBlockVersionModalForm } from '../Form/TeachingBlockVersionModalForm';
import styles from './TeachingBlockVersionsOverview.module.scss';

export type TeachingBlockVersion = {
  uuid: string;
  active: boolean;
  version: number;
  updatedAt: string;
  description: string;
};

type TeachingBlockVersionsOverviewProps = {
  schoolYearUuid: string;
  onNavigate: () => void;
  subjectContainerUuid: string;
};

export const TeachingBlockVersionsOverview: FC<TeachingBlockVersionsOverviewProps> = ({
  schoolYearUuid,
  onNavigate,
  subjectContainerUuid,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();

  const { confirm, ConfirmationDialog } = useConfirm();

  const [, updateVersion] = useUpdateTeachingBlockVersionsMutation();

  const [, deleteTeachingBlockVersion] = use_DeleteTeachingBlockVersionsMutation();
  const [, deleteTeachingBlockCards] = use_DeleteTeachingBlockCardsMutation();
  const [, deleteTeachingBlockAvailabilityExclusion] = use_DeleteTeachingBlockAvailabilityExclusionMutation();

  const [, duplicateVersion] = useDuplicateTeachingBlockVersionMutation();
  const [, deleteTeachingBlock] = use_DeleteTeachingBlocksMutation();

  const [loading, setLoading] = useState<string | null>(null);
  const [versionToEdit, setVersionToEdit] = useState<TeachingBlockVersion | null>(null);

  const context = useMemo(() => {
    return {
      additionalTypenames: [
        'TeachingBlockVersion',
        'TeachingBlock',
        'SubjectContainer',
        'TeachingBlockCard',
        'LessonClass',
      ],
    };
  }, []);

  const [{ data }] = useTeachingBlockDraftQuery({
    variables: {
      where: {
        schoolYear: {
          uuid: schoolYearUuid,
        },
        subjectContainer: {
          uuid: subjectContainerUuid,
        },
      },
    },
    context,
  });

  const memoizedData: TeachingBlockVersion[] = useMemo(() => {
    const versions =
      data?.teachingBlocks[0] && data?.teachingBlocks[0].versions ? data?.teachingBlocks[0].versions : [];

    versions.sort((a, b) => {
      const aDate = a.updatedAt ? dayjs(a.updatedAt) : dayjs(a.createdAt);
      const bDate = b.updatedAt ? dayjs(b.updatedAt) : dayjs(b.createdAt);
      return aDate.isSameOrBefore(bDate) ? 1 : -1;
    });

    return versions.map((version) => {
      return {
        uuid: version.uuid,
        active: version.active ?? false,
        version: version.version,
        updatedAt: version.updatedAt
          ? dayjs(version.updatedAt).format('DD.MM.YYYY - HH:mm')
          : dayjs(version.createdAt).format('DD.MM.YYYY - HH:mm'),
        description: version.description ?? '',
      };
    });
  }, [data]);

  const activeVersion = memoizedData.find((v) => v.active);
  const otherVersions = memoizedData.filter((t) => t.uuid !== activeVersion?.uuid);

  function onDataNavigate(uuid: string) {
    onNavigate();
    navigate(
      `${t('routes.teachingBlockVersions.slug')}/${uuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
    );
  }

  function onBoardNavigate(uuid: string) {
    onNavigate();
    navigate(`${t('routes.teachingBlockVersions.slug')}/${uuid}/${t('routes.versionPages.board.slug')}`);
  }

  async function onSave(editVersion: Partial<TeachingBlockVersion>) {
    if (!editVersion || !editVersion.uuid) return;
    setLoading(editVersion.uuid);

    if (editVersion.active === true) {
      const currentActiveVersion = memoizedData.find((v) => v.active);
      if (currentActiveVersion) {
        await updateVersion({ where: { uuid: currentActiveVersion.uuid }, update: { active: false } }, context);
      }
    }

    const result = await updateVersion(
      {
        where: { uuid: editVersion.uuid },
        update: {
          active: editVersion.active,
          description: editVersion.description,
        },
      },
      context,
    );
    if (!result || result.error) {
      showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
    } else {
      showSuccessSaveToast();
    }
    setLoading(null);
    setVersionToEdit(null);
  }

  async function onDelete(version: TeachingBlockVersion) {
    const isLastVersion = memoizedData.length === 1;
    const confirmed = await confirm({ title: t('common.delete'), message: t('teachingBlockVersion.deleteMessage') });
    if (!confirmed) return;
    setLoading(version.uuid);

    const r1 = await deleteTeachingBlockAvailabilityExclusion({
      where: {
        teachingBlockVersion: {
          uuid: version.uuid,
        },
      },
    });

    const r2 = await deleteTeachingBlockCards({
      where: { teachingBlockVersion: { uuid: version.uuid } },
    });

    const r3 = await deleteTeachingBlockVersion(
      {
        where: { uuid: version.uuid },
      },
      context,
    );

    const error = r1.error || r2.error || r3.error;

    if (error) {
      showUserErrorToast({ error });
    } else {
      showSuccessDeleteToast();
    }
    setLoading(null);
    if (isLastVersion) {
      await deleteTeachingBlock(
        {
          where: {
            schoolYear: { uuid: schoolYearUuid },
            subjectContainer: { uuid: subjectContainerUuid },
            versionsAggregate: { count: 0 },
          },
        },
        context,
      );
      handleClose();
    }
  }

  function handleClose() {
    setLoading(null);
    setVersionToEdit(null);
  }

  const onDuplicate = async (version: TeachingBlockVersion) => {
    setLoading(version.uuid);
    const result = await duplicateVersion(
      {
        teachingBlockVersionUuid: version.uuid,
        options: {},
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      context,
    );

    if (!result || result.error) {
      showUserErrorToast({ error: result.error });
    } else {
      showSuccessDuplicateToast();
    }
    setLoading(null);
  };

  return (
    <div className={styles['teaching-block-versions-overview']}>
      <div className={styles.active}>
        <div className={styles.hint}>{t('teachingBlockVersion.activeVersion')}</div>
        <div className={styles.list}>
          <div className={styles.header}>
            <div className={styles.version}>{t('teachingBlockVersion.title', { count: 1 })}</div>
            <div className={styles.edited}>{t('common.updatedAt')}</div>
            <div className={styles.name}>{t('common.name')}</div>
            <div className={styles.status}>{t('common.status')}</div>
            <div className={styles.actions}></div>
          </div>
          {activeVersion ? (
            <div className={styles.data}>
              <div className={styles.version}>{activeVersion.version}</div>
              <div className={styles.edited}>{activeVersion.updatedAt}</div>
              <div className={styles.name}>{activeVersion.description}</div>
              {/*<div className={styles.status}>{`${activeVersion.status}%`}</div>*/}
              <div className={styles.actions}>
                <Button hierarchy='tertiary' onClick={() => onDataNavigate(activeVersion.uuid)}>
                  {t('teachingBlockVersion.teachingBlockData')}
                </Button>
                <Button hierarchy='tertiary' className='mr-3' onClick={() => onBoardNavigate(activeVersion.uuid)}>
                  {t('pinboard.title', { count: 1 })}
                </Button>
                <ButtonGroup
                  loading={!!loading && loading === activeVersion.uuid}
                  disabled={!permissions?.canWriteTimetable()}
                >
                  <Button
                    icon={<EditIcon className='small' />}
                    hierarchy='tertiary'
                    onClick={() => setVersionToEdit(activeVersion)}
                    disabled={!permissions?.canWriteTimetable()}
                  />
                  <Dropdown
                    usePortal={false}
                    disabled={!permissions?.canWriteTimetable()}
                    trigger={
                      <Button
                        disabled={!permissions?.canWriteTimetable()}
                        icon={<DotsHorizontalIcon className='small' />}
                        hierarchy='tertiary'
                      />
                    }
                  >
                    <DropdownMenu
                      data={[
                        {
                          label: t('common.deactivate'),
                          onClick: () => onSave({ ...activeVersion, active: false }),
                        },
                        {
                          label: t('common.duplicate'),
                          onClick: () => onDuplicate(activeVersion),
                        },
                        {
                          type: 'ruler',
                        },
                        {
                          label: t('common.delete'),
                          type: 'default',
                          color: 'error',
                          disabled: true,
                          onClick: () => onDelete(activeVersion),
                        },
                      ]}
                    />
                  </Dropdown>
                </ButtonGroup>
              </div>
            </div>
          ) : (
            <EmptyState
              title={t('teachingBlockVersion.noActiveVersion')}
              subtitle={t('teachingBlockVersion.noActiveVersionHint')}
              size='small'
              forcedHeight='100px'
              hideIcon
            />
          )}
        </div>
      </div>
      <div className={styles.versions}>
        <div className={styles.hint}>{t('teachingBlockVersion.pastVersions')}</div>
        <div className={styles.list}>
          <div className={styles.header}>
            <div className={styles.version}>{t('teachingBlockVersion.title', { count: 1 })}</div>
            <div className={styles.edited}>{t('common.updatedAt')}</div>
            <div className={styles.name}>{t('common.name')}</div>
            <div className={styles.status}>{t('common.status')}</div>
            <div className={styles.actions}></div>
          </div>
          {otherVersions.length === 0 ? (
            <EmptyState
              title={t('teachingBlockVersion.noPastVersions')}
              subtitle={t('teachingBlockVersion.noPastVersionsHint')}
              size='small'
              forcedHeight='100px'
              hideIcon
            />
          ) : (
            <div className={classNames(styles['data-wrapper'], 'has-scrollbar')}>
              {otherVersions.map((version) => {
                return (
                  <div className={styles.data} key={version.uuid}>
                    <div className={styles.version}>{version.version}</div>
                    <div className={styles.edited}>{version.updatedAt}</div>
                    <div className={styles.name}>{version.description}</div>
                    {/*<div className={styles.status}>{`${version.status}%`}</div>*/}
                    <div className={styles.actions}>
                      <Button hierarchy='tertiary' onClick={() => onDataNavigate(version.uuid)}>
                        {t('teachingBlockVersion.teachingBlockData')}
                      </Button>
                      <Button hierarchy='tertiary' className='mr-3' onClick={() => onBoardNavigate(version.uuid)}>
                        {t('pinboard.title', { count: 1 })}
                      </Button>
                      <ButtonGroup disabled={!permissions?.canWriteTimetable()}>
                        <Button
                          icon={<EditIcon className='small' />}
                          hierarchy='tertiary'
                          onClick={() => setVersionToEdit(version)}
                        />
                        <Dropdown
                          usePortal={false}
                          trigger={<Button icon={<DotsHorizontalIcon className='small' />} hierarchy='tertiary' />}
                        >
                          <DropdownMenu
                            data={[
                              {
                                label: t('common.activate'),
                                onClick: () => onSave({ ...version, active: true }),
                              },
                              {
                                label: t('common.duplicate'),
                                onClick: () => onDuplicate(version),
                              },
                              {
                                type: 'ruler',
                              },
                              {
                                label: t('common.delete'),
                                type: 'default',
                                color: 'error',
                                onClick: () => onDelete(version),
                              },
                            ]}
                          />
                        </Dropdown>
                      </ButtonGroup>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <TeachingBlockVersionModalForm
        editVersion={versionToEdit}
        loading={!!loading}
        onSave={onSave}
        onClose={handleClose}
      />
      <ConfirmationDialog />
    </div>
  );
};
